import { getRouteApi } from '@tanstack/react-router'
import { useCallback } from 'react'
import { z } from 'zod'

import { BACKGROUND_TABS, BackgroundEditor, type BackgroundTab } from '@/features/backgrounds/BackgroundEditor'

/**
 * `/backgrounds/<uid…>?tab=places|scenery|details` — one background's editor.
 *
 * Stage uids contain `/` and `:` (`bg:nature/meadow`, `live:beach/beach_day`), so the route is a
 * splat like the annotator's (`/annotate/$kind/$`): `/backgrounds/$` hands the rest of the path
 * back as the uid, verbatim.
 */
const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)

export const backgroundSearchSchema = z.object({ tab: lenient(z.enum(BACKGROUND_TABS)) })
export type BackgroundSearch = z.infer<typeof backgroundSearchSchema>

export function validateBackgroundSearch(input: Record<string, unknown>): BackgroundSearch {
  return backgroundSearchSchema.parse(input)
}

export const BACKGROUND_ROUTE_ID = '/backgrounds/$' as const
const route = getRouteApi(BACKGROUND_ROUTE_ID)

/** The path of a background's editor; the uid goes in verbatim. */
export function backgroundHref(uid: string): string {
  return `/backgrounds/${uid}`
}

export function BackgroundRoutePage() {
  const uid = route.useParams()._splat ?? ''
  const { tab } = route.useSearch()
  const navigate = route.useNavigate()
  const onTabChange = useCallback((next: BackgroundTab) => void navigate({ search: { tab: next === 'places' ? undefined : next }, replace: true }), [navigate])
  // Keyed by uid: another background remounts the page (fresh draft, fresh queries).
  return <BackgroundEditor key={uid} uid={uid} tab={tab} onTabChange={onTabChange} />
}
